import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PageChrome, ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useRecordFinds, useRecordsState, useDismissFind, findHeadline } from '@/hooks/useRecords'
import { FindCard, FindTag, DocLink, RecordsBadge, filedLine, sortFinds } from '@/components/records/FindBits'
import { SplitQueue } from '@/components/alerts/SplitQueue'
import { dayOf, longDay, useQueueBuild } from '@/components/alerts/ui'
import type { RecordFind } from '@/components/alerts/types'
import '@/styles/alerts.css'

/**
 * Record finds (/app/records): the public records queue. Brian's records.html
 * v1.1 and desk-split.js; docs/PUBLIC-RECORDS.md v0.3.
 *
 * What Monday's check found in the public sources for the account's places:
 * type and place, the filed date, plain facts, the next date, the document
 * linked. Dismiss, or Build (the normal story, 12 from the week, marked Public
 * record). Nothing expires; a find reaches the brief only when built. The
 * check's line says what was checked, found, and couldn't be reached.
 */
export function RecordFindsPage() {
  const desk = useIsDesktop()
  const nav = useNavigate()
  const { data: r } = useRecordsState()
  const { data: finds, isLoading } = useRecordFinds()
  const dismiss = useDismissFind()
  const qb = useQueueBuild(findHeadline, [['record-finds'], ['records-state']])

  const waiting = useMemo(() => sortFinds((finds ?? []).filter((f) => f.status === 'new')), [finds])
  const done = useMemo(() => (finds ?? []).filter((f) => f.status !== 'new').sort((a, b) => Date.parse(b.done_at ?? b.created_at) - Date.parse(a.done_at ?? a.created_at)), [finds])
  const n = waiting.length
  const c = r?.last_check

  const doDismiss = (f: RecordFind) => { dismiss.mutate(f.id); qb.toast.show(<>Dismissed. <b>It stays under Earlier.</b></>) }
  const doBuild = (f: RecordFind) => qb.build(f.id, () => qb.toast.show(<>Building. <b>Emma is writing it; it lands in your Library, marked Public record.</b></>))

  const notes = (
    <>
      {r?.paused && <div className="gga-note paused">Paused. No sources are checked and nothing is charged; what’s here stays buildable.</div>}
      {r && c && (
        <div className="gga-chk"><b>Checked {longDay(c.at)}</b> · {c.checked} {c.checked === 1 ? 'source' : 'sources'} ({c.charged} {c.charged === 1 ? 'seed' : 'seeds'}), {c.found} new
          {c.unreachable ? ` · ${c.unreachable} couldn’t be reached (free)` : ''}{c.skipped ? ` · ${c.skipped} skipped, over the budget` : ''}. Next check {longDay(r.next_check)}, early morning.</div>
      )}
      {r && !c && r.on > 0 && <div className="gga-chk">Your first check is <b>{longDay(r.next_check)}</b>, early morning.</div>}
    </>
  )
  const empty = <div className="gga-none"><b>Nothing waiting.</b>{r && r.on === 0
    ? <>Turn on the records to look for in <Link to={ROUTES.recordsSettings}>Records settings</Link>.</>
    : 'Your sources are checked every Monday. What’s new waits here until you build or dismiss it.'}</div>
  const earlier = done.length > 0 && (
    <details className="gga-earlier">
      <summary>Earlier · {done.length}</summary>
      {done.map((f) => (
        <div className="row" key={f.id}>
          <span className={`st${f.status === 'built' ? ' built' : ''}`}>{f.status === 'built' ? 'Built' : 'Dismissed'}</span>
          {f.status === 'built' && f.story_id ? <Link to={ROUTES.story(f.story_id)}>{f.title}</Link> : <Link to={ROUTES.recordFind(f.id)}>{f.title}</Link>}
        </div>
      ))}
    </details>
  )

  return (
    <>
      <PageChrome title="Record finds" navKey="records" actions={<Link className="gg-act" to={ROUTES.recordsSettings}>Records settings</Link>} />
      <div className={`gg gga${desk ? ' gga--wide' : ''}`}>
        <div className="gga-head">
          <div className="sub">{isLoading ? 'Loading' : n ? `${n} waiting · nothing expires` : 'Nothing waiting · nothing expires'}</div>
          {r && <RecordsBadge r={r} onOpen={() => nav(ROUTES.recordsSettings)} />}
        </div>
        {notes}
        {desk ? (
          <SplitQueue items={waiting} cost={qb.cost}
            card={(f, sel, select) => <FindCard key={f.id} f={f} selected={sel} cost={qb.cost} onOpen={select} onDismiss={() => doDismiss(f)} onBuild={() => doBuild(f)} />}
            detail={(f) => (
              <>
                <FindTag f={f} />
                <h2>{f.title}</h2>
                <div className="by"><b><DocLink f={f} /></b> · {filedLine(f)}</div>
                {f.facts.map((p, i) => <div className="gga-fact" key={i}>{p}</div>)}
                {f.next_date && <div className="meta"><b>{dayOf(f.next_date)}</b> · {f.next_what}</div>}
              </>
            )}
            onBuild={doBuild} onDismiss={doDismiss}
            after={<>{!n && !isLoading && empty}{earlier}</>}
          />
        ) : (
          <>
            {waiting.map((f) => <FindCard key={f.id} f={f} cost={qb.cost} onOpen={() => nav(ROUTES.recordFind(f.id))} onDismiss={() => doDismiss(f)} onBuild={() => doBuild(f)} />)}
            {!n && !isLoading && empty}
            {earlier}
          </>
        )}
      </div>
      {qb.confirm}
      {qb.toast.node}
    </>
  )
}
